import {
  ApolloCache,
  DefaultContext,
  FetchResult,
  gql,
  MutationFunctionOptions,
  useApolloClient,
} from "@apollo/client"
import { TFunction } from "@einride/saga-core"
import { showNotification } from "@mantine/notifications"
import {
  AggregationInterval,
  Comparison,
  CreateReportMutation,
  CreateReportRequest,
  DateSpanInterval,
  DeleteReportMutation,
  DeleteReportRequest,
  Exact,
  ReportDocument,
  ReportFragment,
  ReportQuery,
  UpdateReportMutation,
  UpdateReportRequest,
  useCreateReportMutation,
  useDeleteReportMutation,
  useReportQuery,
  useUpdateReportMutation,
  Visualization,
} from "gen/graphql"
import { toReportResourceName } from "lib/resourceNames"
import { translateMetricId } from "lib/translations"
import { Dispatch, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { getGroupColumnName } from "../components/Sidebar/translate"

interface useReportStateResponse {
  loading: boolean
  isDirty: boolean
  report: ReportFragment
  setReport: Dispatch<SetStateAction<ReportFragment>>
  saveReport: (
    options?:
      | MutationFunctionOptions<
          UpdateReportMutation,
          Exact<{
            input: UpdateReportRequest
          }>,
          DefaultContext,
          ApolloCache<UpdateReportMutation>
        >
      | undefined,
  ) => Promise<FetchResult<UpdateReportMutation, Record<string, unknown>, Record<string, unknown>>>
  createReport: (
    options?:
      | MutationFunctionOptions<
          CreateReportMutation,
          Exact<{
            input: CreateReportRequest
          }>,
          DefaultContext,
          ApolloCache<CreateReportMutation>
        >
      | undefined,
  ) => Promise<FetchResult<CreateReportMutation, Record<string, unknown>, Record<string, unknown>>>
  deleteReport: (
    options?:
      | MutationFunctionOptions<
          DeleteReportMutation,
          Exact<{
            input: DeleteReportRequest
          }>,
          DefaultContext,
          ApolloCache<DeleteReportMutation>
        >
      | undefined,
  ) => Promise<FetchResult<DeleteReportMutation, Record<string, unknown>, Record<string, unknown>>>
}

export const NEW_REPORT = "new"

export const useReportState = (reportId: string | undefined): useReportStateResponse => {
  const { t } = useTranslation()
  const client = useApolloClient()
  const reportName = toReportResourceName(reportId ?? "")
  const navigate = useNavigate()
  const [report, setReport] = useState<ReportFragment>({
    name: reportId ?? "",
    displayName: reportId === NEW_REPORT ? t("reportPage.untitledReport") : "",
    template: false,
    metrics: [
      {
        metricId: "",
        unit: "",
        aggregationInterval: AggregationInterval.Day,
        visualization: Visualization.LineGraph,
        datespan: {
          interval: DateSpanInterval.YearToDate,
        },
        selectedGroups: [],
      },
    ],
  })

  const [createReport, { loading: createReportLoading }] = useCreateReportMutation({
    variables: {
      input: {
        displayName: report.displayName,
        description: report.description ?? "",
        metrics: report.metrics,
      },
    },
    onCompleted: (data) => {
      setReport(data.CreateReport)
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "SearchReports" })
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "QueryData" })
    },
    onError: () => {
      showNotification({
        message: t("reportPage.errors.createReport"),
        color: "red",
      })
    },
  })

  const [saveReport, { loading: updateReportLoading }] = useUpdateReportMutation({
    variables: {
      input: {
        name: report.name,
        displayName: report.displayName,
        description: report.description ?? "",
        metrics: report.metrics,
      },
    },
    onCompleted: () => {
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "SearchReports" })
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "QueryData" })
    },
    onError: () => {
      showNotification({
        message: t("reportPage.errors.updateReport"),
        color: "red",
      })
    },
  })

  const [deleteReport, { loading: deleteReportLoading }] = useDeleteReportMutation({
    variables: {
      input: {
        name: report.name,
      },
    },
    onCompleted: () => {
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "SearchReports" })
      client.cache.evict({ id: "ROOT_QUERY", fieldName: "QueryData" })
    },
    onError: () => {
      showNotification({
        message: t("reportPage.errors.deleteReport"),
        color: "red",
      })
    },
  })

  const { loading: getReportLoading } = useReportQuery({
    variables: {
      request: {
        name: reportName ?? "",
      },
    },
    onCompleted: (data) => {
      setReport(data.Report)
    },
    onError: (error) => {
      const notFoundError = error.graphQLErrors.find((err) => err.extensions?.code === "NotFound")
      showNotification({
        message: notFoundError
          ? t("reportPage.errors.reportNotFound")
          : t("reportPage.errors.getReport"),
        color: "red",
      })
      navigate("/reports")
    },
    skip: !reportName || reportName === toReportResourceName(NEW_REPORT),
  })

  const cachedReport: ReportQuery | null = client.cache.readQuery({
    query: ReportDocument,
    variables: {
      request: {
        name: reportName ?? "",
      },
    },
  })
  const isReportChanged = JSON.stringify(cachedReport?.Report) !== JSON.stringify(report)

  return {
    loading: createReportLoading || updateReportLoading || deleteReportLoading || getReportLoading,
    report,
    setReport,
    saveReport,
    createReport,
    deleteReport,
    isDirty: isReportChanged,
  }
}

export const REPORT_DATESPAN_FRAGMENT = gql`
  fragment DateSpan on MetricDateSpan {
    startDate {
      ...Date
    }
    endDate {
      ...Date
    }
    interval
  }
`

export const COMPARISON_FRAGMENT = gql`
  fragment Comparison on Comparison {
    interval
    metric
  }
`

export const REPORT_FRAGMENT = gql`
  fragment Report on Report {
    name
    displayName
    description
    metrics {
      filterSelection {
        filterAttributeId
        filterTermIds
      }
      groupAttributeId
      metricId
      unit
      visualization
      groupsAsShareOfTotal
      comparedTo {
        comparison {
          ...Comparison
        }
        relative
      }
      datespan {
        ...DateSpan
      }
      aggregationInterval
      selectedGroups
    }
    template
  }
`

export const GET_REPORT_QUERY = gql`
  query Report($request: GetReportRequest!) {
    Report(request: $request) {
      ...Report
    }
  }
`

export const CREATE_REPORT = gql`
  mutation CreateReport($input: CreateReportRequest!) {
    CreateReport(input: $input) {
      ...Report
    }
  }
`

export const UPDATE_REPORT = gql`
  mutation UpdateReport($input: UpdateReportRequest!) {
    UpdateReport(input: $input) {
      ...Report
    }
  }
`

export const DUPLICATE_REPORT = gql`
  mutation DuplicateReport($input: DuplicateReportRequest!) {
    DuplicateReport(input: $input) {
      ...Report
    }
  }
`

export const DELETE_REPORT = gql`
  mutation DeleteReport($input: DeleteReportRequest!) {
    DeleteReport(input: $input) {
      ...Report
    }
  }
`

export function generateDisplayName(report: ReportFragment, t: TFunction<"explore">): string {
  let reportName = ""

  const metric = report.metrics[0]
  if (metric.metricId) {
    reportName += translateMetricId(metric.metricId, t)
  }

  if (metric.groupAttributeId) {
    const groupName = getGroupColumnName(metric.groupAttributeId, t).toLowerCase()
    reportName += ` ${t("common.groupedBy")} ${groupName}`
  }

  return reportName
}

export function getReportDisplayName(
  previousReportState: ReportFragment,
  report: ReportFragment,
  t: TFunction<"explore">,
): string {
  // If the previous name was untitled or generated, also generate the new one
  if (
    previousReportState.displayName === "" ||
    previousReportState.displayName === t("reportPage.untitledReport") ||
    previousReportState.displayName === generateDisplayName(previousReportState, t)
  ) {
    return generateDisplayName(report, t)
  }

  return report.displayName
}

export function getReportDescription(
  previousReportState: ReportFragment,
  report: ReportFragment,
  t: TFunction<"explore">,
): string | null {
  const previousMetric = previousReportState.metrics[0]
  const metric = report.metrics[0]
  if (
    !report.description &&
    metric.metricId === "transport_activity" &&
    previousMetric.metricId !== "transport_activity"
  ) {
    return t("metricsDescription.transportActivity")
  }
  if (
    report.description === t("metricsDescription.transportActivity") &&
    metric.metricId !== "transport_activity" &&
    previousMetric.metricId === "transport_activity"
  ) {
    return null
  }
  return report.description ?? null
}

export function isNewReport(report: ReportFragment): boolean {
  return !report.name || report.name === NEW_REPORT
}

export function havingComparison(
  comparison: Comparison | undefined | null,
): comparison is Comparison {
  return comparison?.interval != null || comparison?.metric != null
}
